import type { EnumIdentificationCard } from '@/shared/catalogs/identifications'
import { numberToLetters } from './numbers.lib'

export const cuiIsValid = (cui: string) => {
  if (!cui) return false

  const cuiRegExp = /^[0-9]{4}\s?[0-9]{5}\s?[0-9]{4}$/

  if (!cuiRegExp.test(cui)) return false

  const numberId = cui.replace(/\s/, '')

  const depto = parseInt(numberId.substring(9, 11), 10)
  const muni = parseInt(numberId.substring(11, 13))
  const numero = numberId.substring(0, 8)
  const verificador = parseInt(numberId.substring(8, 9))

  // Se asume que la codificación de Municipios y
  // departamentos es la misma que esta publicada en
  // http://goo.gl/EsxN1a

  // Listado de municipios actualizado segun:
  // http://goo.gl/QLNglm

  // Este listado contiene la cantidad de municipios
  // existentes en cada departamento para poder
  // determinar el código máximo aceptado por cada
  // uno de los departamentos.

  const munisPorDepto = [
    /* 01 - Guatemala tiene:      */ 17 /* municipios. */,
    /* 02 - El Progreso tiene:    */ 8 /* municipios. */,
    /* 03 - Sacatepéquez tiene:   */ 16 /* municipios. */,
    /* 04 - Chimaltenango tiene:  */ 16 /* municipios. */,
    /* 05 - Escuintla tiene:      */ 13 /* municipios. */,
    /* 06 - Santa Rosa tiene:     */ 14 /* municipios. */,
    /* 07 - Sololá tiene:         */ 19 /* municipios. */,
    /* 08 - Totonicapán tiene:    */ 8 /* municipios. */,
    /* 09 - Quetzaltenango tiene: */ 24 /* municipios. */,
    /* 10 - Suchitepéquez tiene:  */ 21 /* municipios. */,
    /* 11 - Retalhuleu tiene:     */ 9 /* municipios. */,
    /* 12 - San Marcos tiene:     */ 30 /* municipios. */,
    /* 13 - Huehuetenango tiene:  */ 32 /* municipios. */,
    /* 14 - Quiché tiene:         */ 21 /* municipios. */,
    /* 15 - Baja Verapaz tiene:   */ 8 /* municipios. */,
    /* 16 - Alta Verapaz tiene:   */ 17 /* municipios. */,
    /* 17 - Petén tiene:          */ 14 /* municipios. */,
    /* 18 - Izabal tiene:         */ 5 /* municipios. */,
    /* 19 - Zacapa tiene:         */ 11 /* municipios. */,
    /* 20 - Chiquimula tiene:     */ 11 /* municipios. */,
    /* 21 - Jalapa tiene:         */ 7 /* municipios. */,
    /* 22 - Jutiapa tiene:        */ 17 /* municipios. */
  ]

  if (depto === 0 || muni === 0) {
    // console.log("CUI con código de municipio o departamento inválido.");
    return false
  }

  if (depto > munisPorDepto.length) {
    // console.log("CUI con código de departamento inválido.");
    return false
  }

  if (muni > munisPorDepto[depto - 1]) {
    // console.log("CUI con código de municipio inválido.");
    return false
  }

  // Se verifica el correlativo con base
  // en el algoritmo del complemento 11.
  let total = 0

  for (let i = 0; i < numero.length; i++) {
    total += parseInt(numero[i]) * (i + 2)
  }

  const modulo = total % 11

  // console.log("CUI con módulo: " + modulo);
  return modulo === verificador
}

interface FormatOfTypeDocument {
  numbers: {
    full: string
    correlative?: {
      full: string
      firstPart: string
      secondPart: string
      secondPartWithChecker: string
    }
    checker?: string
    department?: string
    municipality?: string
  }
  format: string
  letter: string
}

export const getFormatOfTypeDocument = ({
  type,
  number
}: {
  type: EnumIdentificationCard
  number: string
}): FormatOfTypeDocument => {
  if (type === 'D' || type === 'CN') {
    if (cuiIsValid(number)) {
      const numbers = {
        full: number,
        correlative: {
          full: number.substring(0, 8),
          firstPart: number.substring(0, 4),
          secondPart: number.substring(4, 8),
          secondPartWithChecker: number.substring(4, 9)
        },
        checker: number.substring(8, 9),
        department: number.substring(9, 11),
        municipality: number.substring(11, 13)
      }

      const depAndMun = `${numbers.department}${numbers.municipality}`
      const format = `${numbers.correlative.firstPart} ${numbers.correlative.secondPartWithChecker} ${depAndMun}`

      const textDepAndMun =
        numbers.department[0] === '0'
          ? `Cero ${numberToLetters(depAndMun)}`
          : numberToLetters(depAndMun)

      const letter = `${numberToLetters(numbers.correlative.firstPart)}, ${numberToLetters(numbers.correlative.secondPartWithChecker)}, ${textDepAndMun}`
      return {
        numbers,
        format,
        letter
      }
    }
  }

  return {
    numbers: {
      full: number
    },
    format: number,
    letter: /^[0-9]*$/.test(number) ? numberToLetters(number) : number
  }
}
